import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { Logo } from "./Logo";

/**
 * Global footer.
 *
 * Wyoming LLC, US-first. No VAT. Address + email + phone. Includes
 * legal nav (Terms / Privacy) and the explore nav, so every page in
 * the marketing surface has crawlable internal links to the legal
 * pages and the top-level sections.
 */
export const Footer = () => {
    // Hard-coded company contacts. Source of truth, not pulled from /api/company
    // anymore so the footer renders correctly even when the company endpoint is
    // slow or unreachable.
    const COMPANY = {
        legalName: "Cybersecurity Forefront LLC",
        email: "info@cybersecurityforefront.com",
        phone: "+1 (628) 600-9527",
        addressLine1: "30 N Gould St, Ste N",
        addressLine2: "Sheridan, WY 82801",
        country: "United States",
    };

    return (
        <footer
            className="relative mt-24 md:mt-32 border-t border-white/[0.05] bg-surface/40"
            data-testid="site-footer"
        >
            <div className="mx-auto max-w-7xl px-5 md:px-10 py-12 grid gap-10 md:grid-cols-12">
                {/* Brand */}
                <div className="md:col-span-4">
                    <Logo variant="full" size="md" />
                    <p className="mt-5 text-[13px] text-text-muted leading-relaxed max-w-sm">
                        The chat-first firewall configurator and Client IPAM for MSPs and
                        security engineers. Free compliance tools, gated provisioning, no
                        toil.
                    </p>
                </div>

                {/* Product */}
                <div className="md:col-span-2">
                    <h4 className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim mb-4">
                        Product
                    </h4>
                    <ul className="space-y-2.5">
                        {[
                            { to: "/tools", label: "Free Tools" },
                            { to: "/zeroconf", label: "Firewall Configurator" },
                            { to: "/ipam", label: "Client IPAM" },
                            { to: "/pricing", label: "Pricing" },
                        ].map((l) => (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    className="text-[13px] text-text-muted hover:text-accent-cyan transition-colors duration-200"
                                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Company */}
                <div className="md:col-span-2">
                    <h4 className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim mb-4">
                        Company
                    </h4>
                    <ul className="space-y-2.5">
                        {[
                            { to: "/about", label: "About" },
                            { to: "/contact", label: "Contact" },
                        ].map((l) => (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    className="text-[13px] text-text-muted hover:text-accent-cyan transition-colors duration-200"
                                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Legal */}
                <div className="md:col-span-2">
                    <h4 className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim mb-4">
                        Legal
                    </h4>
                    <ul className="space-y-2.5">
                        <li>
                            <Link
                                to="/terms"
                                className="text-[13px] text-text-muted hover:text-accent-cyan transition-colors duration-200"
                                data-testid="footer-link-terms"
                            >
                                Terms of Service
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/privacy"
                                className="text-[13px] text-text-muted hover:text-accent-cyan transition-colors duration-200"
                                data-testid="footer-link-privacy"
                            >
                                Privacy Policy
                            </Link>
                        </li>
                    </ul>
                </div>

                {/* Reach us */}
                <div className="md:col-span-2">
                    <h4 className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim mb-4">
                        Reach us
                    </h4>
                    <ul className="space-y-3">
                        <li>
                            <a
                                href={`mailto:${COMPANY.email}`}
                                className="inline-flex items-center gap-2 text-[13px] text-text-muted hover:text-accent-cyan transition-colors duration-200 break-all"
                                data-testid="footer-email"
                            >
                                <Mail className="w-3.5 h-3.5 text-text-dim shrink-0" />
                                <span>{COMPANY.email}</span>
                            </a>
                        </li>
                        <li>
                            <a
                                href={`tel:${COMPANY.phone.replace(/\s+/g, "")}`}
                                className="inline-flex items-center gap-2 text-[13px] text-text-muted hover:text-accent-cyan transition-colors duration-200"
                                data-testid="footer-phone"
                            >
                                <Phone className="w-3.5 h-3.5 text-text-dim shrink-0" />
                                {COMPANY.phone}
                            </a>
                        </li>
                        <li
                            className="flex items-start gap-2 text-[12px] text-text-muted leading-relaxed max-w-xs"
                            data-testid="footer-address"
                        >
                            <MapPin className="w-3.5 h-3.5 text-text-dim mt-0.5 shrink-0" />
                            <span>
                                {COMPANY.legalName}
                                <br />
                                {COMPANY.addressLine1}
                                <br />
                                {COMPANY.addressLine2}
                                <br />
                                {COMPANY.country}
                            </span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/[0.04]">
                <div className="mx-auto max-w-7xl px-5 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-2">
                    <p className="text-[11px] font-mono tracking-wider uppercase text-text-dim">
                        © {new Date().getFullYear()} {COMPANY.legalName} · All rights
                        reserved
                    </p>
                    <p className="text-[11px] font-mono tracking-wider uppercase text-text-dim">
                        Wyoming, USA · cybersecurityforefront.com
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
